import React, { useState, useEffect } from 'react';
import { db, storage } from '../../../utils/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, updateDoc, doc, deleteDoc, getDocs, query, orderBy, serverTimestamp, writeBatch } from 'firebase/firestore';
import { Plus, Edit2, Trash2, Check, X, Loader, Upload, Image as ImageIcon } from 'lucide-react';
import { Lookbook, Product } from '../../../types';

const LookbookManager: React.FC = () => {
  const [lookbooks, setLookbooks] = useState<Lookbook[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Form State
  const [title, setTitle] = useState('');
  const [season, setSeason] = useState('');
  const [description, setDescription] = useState('');
  const [images, setImages] = useState<string[]>([]);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [isActive, setIsActive] = useState(false);

  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch Lookbooks
      const lbQuery = query(collection(db, 'lookbooks'), orderBy('createdAt', 'desc'));
      const lbSnap = await getDocs(lbQuery);
      const fetchedLookbooks = lbSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Lookbook));
      setLookbooks(fetchedLookbooks);

      // Fetch Products (for selection)
      const pQuery = query(collection(db, 'products'), orderBy('title', 'asc'));
      const pSnap = await getDocs(pQuery);
      const fetchedProducts = pSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Product));
      setProducts(fetchedProducts);

    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    const newImageUrls: string[] = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const storageRef = ref(storage, `lookbooks/${Date.now()}_${file.name}`);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        newImageUrls.push(url);
      }
      setImages(prev => [...prev, ...newImageUrls]);
    } catch (error) {
      console.error("Error uploading images:", error);
      setMessage({ type: 'error', text: 'Failed to upload images.' });
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const toggleProductSelection = (productId: string) => {
    setSelectedProductIds(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const resetForm = () => {
    setTitle('');
    setSeason('');
    setDescription('');
    setImages([]);
    setSelectedProductIds([]);
    setIsActive(false);
    setEditingId(null);
    setMessage(null);
  };

  const handleEdit = (lb: Lookbook) => {
    setEditingId(lb.id);
    setTitle(lb.title);
    setSeason(lb.season);
    setDescription(lb.description);
    setImages(lb.images || []);
    setSelectedProductIds(lb.productIds || []);
    setIsActive(lb.active);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this lookbook?')) return;
    try {
      await deleteDoc(doc(db, 'lookbooks', id));
      setLookbooks(prev => prev.filter(lb => lb.id !== id));
      setMessage({ type: 'success', text: 'Lookbook deleted.' });
    } catch (error) {
      console.error("Error deleting lookbook:", error);
      setMessage({ type: 'error', text: 'Failed to delete.' });
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title || images.length === 0) {
      setMessage({ type: 'error', text: 'Title and at least one image are required.' });
      return;
    }

    setUploading(true); // Re-use uploading state for saving
    try {
      const lookbookData: any = {
        title,
        season,
        description,
        images,
        productIds: selectedProductIds,
        active: isActive,
        // createdAt: editingId ? undefined : serverTimestamp(), // REMOVED to avoid undefined error
      };

      if (!editingId) {
          lookbookData.createdAt = serverTimestamp();
      }

      // If setting this one to active, we might want to de-activate others?
      // For simplicity, let's allow multiple active or just trust the user to manage it.
      // Or we can enforcing single active:
      if (isActive) {
        const batch = writeBatch(db);
        lookbooks.forEach(lb => {
          if (lb.active && lb.id !== editingId) {
             const lbRef = doc(db, 'lookbooks', lb.id);
             batch.update(lbRef, { active: false });
          }
        });
        await batch.commit();
      }

      console.log("Saving Lookbook Data:", lookbookData); // DEBUG

      if (editingId) {
        await updateDoc(doc(db, 'lookbooks', editingId), lookbookData);
        setMessage({ type: 'success', text: 'Lookbook updated successfully!' });
      } else {
        await addDoc(collection(db, 'lookbooks'), lookbookData);
        setMessage({ type: 'success', text: 'Lookbook created successfully!' });
      }

      await fetchData(); // Refresh list
      resetForm();

    } catch (error: any) {
      console.error("Error saving lookbook:", error);
      console.log("Error details:", error.code, error.message); // DEBUG
      setMessage({ type: 'error', text: `Failed to save lookbook: ${error.message}` });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-12">
      {/* Form Section */}
      <div className="bg-white p-6 border border-gray-200">
        <div className="flex justify-between items-center mb-6">
            <h2 className="font-heading font-bold text-xl flex items-center gap-2">
                {editingId ? <Edit2 size={20} /> : <Plus size={20} />}
                {editingId ? 'EDIT LOOKBOOK' : 'CREATE LOOKBOOK'}
            </h2>
            {editingId && (
                <button onClick={resetForm} className="text-xs text-gray-500 hover:text-black">Cancel</button>
            )}
        </div>

        {message && (
            <div className={`p-3 mb-6 flex items-center gap-2 text-sm font-bold ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {message.type === 'success' ? <Check size={16} /> : <X size={16} />}
                {message.text}
            </div>
        )}

        <form onSubmit={handleSave} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Title</label>
                    <input 
                        type="text" 
                        value={title} 
                        onChange={e => setTitle(e.target.value)}
                        className="w-full p-3 border border-gray-300 focus:outline-none focus:border-black"
                        placeholder="Summer Collection 2026"
                    />
                </div>
                <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Season</label>
                    <input 
                        type="text" 
                        value={season} 
                        onChange={e => setSeason(e.target.value)}
                        className="w-full p-3 border border-gray-300 focus:outline-none focus:border-black"
                        placeholder="SS26"
                    />
                </div>
            </div>

            <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Description</label>
                <textarea 
                    value={description} 
                    onChange={e => setDescription(e.target.value)}
                    className="w-full p-3 border border-gray-300 focus:outline-none focus:border-black h-32 resize-none"
                    placeholder="Describe the theme and inspiration..."
                />
            </div>

            {/* Image Upload */}
            <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Lookbook Images</label>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-4">
                    {images.map((url, idx) => (
                        <div key={idx} className="relative group aspect-[3/4]">
                            <img src={url} alt={`Look ${idx}`} className="w-full h-full object-cover border border-gray-200" />
                            <button 
                                type="button"
                                onClick={() => removeImage(idx)}
                                className="absolute top-1 right-1 bg-white/90 p-1 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                                <X size={14} />
                            </button>
                        </div>
                    ))}
                    <label className="border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:border-black hover:bg-gray-50 transition-colors aspect-[3/4]">
                        {uploading ? <Loader className="animate-spin text-gray-400" /> : <Upload className="text-gray-400" />}
                        <span className="text-xs font-bold text-gray-400 mt-2 uppercase">Add Images</span>
                        <input type="file" multiple accept="image/*" className="hidden" onChange={handleImageUpload} disabled={uploading} />
                    </label>
                </div>
            </div>

            {/* Product Selection */}
            <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Related Products</label>
                <div className="border border-gray-300 max-h-64 overflow-y-auto p-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {products.map(product => (
                        <label key={product.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 cursor-pointer border border-transparent hover:border-gray-100">
                            <input 
                                type="checkbox" 
                                checked={selectedProductIds.includes(product.id)}
                                onChange={() => toggleProductSelection(product.id)}
                                className="accent-black"
                            />
                            <img src={product.image} alt="" className="w-8 h-8 object-cover rounded-sm" />
                            <span className="text-sm truncate">{product.title}</span>
                        </label>
                    ))}
                </div>
                <p className="text-[10px] text-gray-400 mt-1 text-right">{selectedProductIds.length} products selected</p>
            </div>

            <div className="flex items-center gap-2">
                <input 
                    type="checkbox" 
                    id="activeToggle"
                    checked={isActive} 
                    onChange={e => setIsActive(e.target.checked)}
                    className="accent-black w-4 h-4"
                />
                <label htmlFor="activeToggle" className="text-sm font-bold uppercase cursor-pointer">Set as Active Lookbook</label>
            </div>

            <button 
                type="submit" 
                disabled={uploading}
                className="bg-black text-white px-8 py-3 font-heading font-bold uppercase tracking-widest text-xs hover:bg-gray-800 disabled:opacity-50 flex items-center justify-center gap-2 w-full md:w-auto"
            >
                {uploading ? <Loader className="animate-spin" size={16} /> : <Check size={16} />}
                SAVE LOOKBOOK
            </button>
        </form>
      </div>

      {/* List Section */}
      <div className="space-y-4">
        <h3 className="text-sm font-heading font-bold uppercase tracking-widest text-gray-500 border-b border-gray-200 pb-2">Existing Lookbooks</h3>
        {loading ? (
            <div className="flex justify-center p-8"><Loader className="animate-spin text-gray-400" /></div>
        ) : (
            <div className="grid gap-4">
                {lookbooks.map(lb => (
                    <div key={lb.id} className={`bg-white p-4 border flex items-center justify-between ${lb.active ? 'border-black ring-1 ring-black' : 'border-gray-200'}`}>
                        <div className="flex items-center gap-4">
                            <div className="w-16 h-16 bg-gray-100 flex items-center justify-center overflow-hidden border border-gray-200">
                                {lb.images?.[0] ? (
                                    <img src={lb.images[0]} alt="" className="w-full h-full object-cover" />
                                ) : (
                                    <ImageIcon size={20} className="text-gray-300" />
                                )}
                            </div>
                            <div>
                                <h4 className="font-bold font-heading">{lb.title} {lb.active && <span className="bg-black text-white text-[10px] px-2 py-0.5 ml-2 rounded-full uppercase">Active</span>}</h4>
                                <p className="text-xs text-gray-500">{lb.season} • {lb.images?.length || 0} Images • {lb.productIds?.length || 0} Products</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={() => handleEdit(lb)} className="p-2 text-gray-400 hover:text-black hover:bg-gray-100 rounded-full transition-colors">
                                <Edit2 size={16} />
                            </button>
                            <button onClick={() => handleDelete(lb.id)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors">
                                <Trash2 size={16} />
                            </button>
                        </div>
                    </div>
                ))}
                {lookbooks.length === 0 && <p className="text-center text-gray-400 text-sm py-8">No lookbooks found.</p>}
            </div>
        )}
      </div>
    </div>
  );
};

export default LookbookManager;
